/**
 * Cloud Operations — Existing Deployments tab.
 *
 * Slim list of live deployments per the unPeg reference. One row per
 * deployment with: mode/provider/region/version, status pill, CPU + memory
 * snapshot, and inline Logs / Scale / Stop or Start actions.
 *
 * Data: listDeployments (paginated; we ask for size=50 v1).
 * Live updates: opens an EventSource on /cloud-operations/events to keep
 * status pills + step counts fresh without a manual refresh.
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { listDeployments } from '../../services/cloudOperationsApi';
import { T, btn, btnSm, Banner, StatusPill, ProviderPill, ModePill, modeLabel } from './_ui.jsx';
import { createReconnectingEventSource } from '../../lib/reconnectingEventSource';

export default function DeployExistingTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    listDeployments({ size: 50 })
      .then((r) => { if (alive) setRows(r?.items || []); })
      .catch((e) => { if (alive) setError(e?.message || String(e)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  // Live updates — keep the page subscribed to ALL-runs SSE so status pills
  // refresh as workflows progress. Self-cleans on unmount.
  useEffect(() => {
    const onDeploymentStatus = (evt) => {
      try {
        const payload = JSON.parse(evt.data);
        if (!payload?.deployment_id) return;
        setRows((prev) => prev.map((r) => r.id === payload.deployment_id
          ? { ...r, status: payload.status ?? r.status }
          : r));
      } catch (_e) { /* ignore */ }
    };
    // Storm-safe reconnect (capped backoff) via the shared helper instead of
    // the browser's native uncapped retry — see lib/reconnectingEventSource.
    const conn = createReconnectingEventSource({
      url: '/api/v1/cloud-operations/events',
      events: { deployment_status: onDeploymentStatus },
    });
    return () => { conn.close(); };
  }, []);

  if (loading) return <Banner color={T.info} bg={T.infoBg}>Loading deployments…</Banner>;
  if (error)   return <Banner color={T.danger} bg={T.dangerBg}>{error}</Banner>;
  if (rows.length === 0) {
    return (
      <Empty />
    );
  }

  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {rows.map((d) => <DeploymentRow key={d.id} d={d} />)}
    </div>
  );
}

function Empty() {
  return (
    <div style={{
      background: T.bgCard,
      border: `1px dashed ${T.stroke}`,
      borderRadius: 10,
      padding: '40px 24px',
      textAlign: 'center',
      color: T.text2,
    }}>
      <div style={{ fontSize: 16, fontWeight: 700, color: T.ink, marginBottom: 6 }}>
        No deployments yet
      </div>
      <div style={{ fontSize: 13, marginBottom: 14 }}>
        Pick a mode above (Prove7 Cloud or Self-Hosted) and use the Configure tab to launch your first one.
      </div>
    </div>
  );
}

function DeploymentRow({ d }) {
  const provider = providerFromDeployment(d);
  const region   = regionFromDeployment(d);
  const version  = d.platform_version || '—';
  const sizeTag  = sizeTagFromDeployment(d);

  // CPU / Memory are placeholder values until a real health snapshot lands —
  // SystemDeploymentRun.output_artifacts will surface these once the helm
  // chart's metrics agent ships.
  const cpu = d.cpu_percent ?? null;
  const mem = d.memory_percent ?? null;

  const stopped = d.status === 'decommissioned' || d.status === 'failed';

  return (
    <div style={{
      background: T.bgCard,
      border: `1px solid ${T.stroke}`,
      borderRadius: 8,
      padding: '14px 16px',
      display: 'grid',
      gridTemplateColumns: '40px 1fr auto auto auto auto auto',
      alignItems: 'center',
      gap: 16,
    }}>
      <div style={{
        width: 32, height: 32, borderRadius: 6,
        background: provider === 'gcp' ? '#ecf3f8'
                  : provider === 'aws' ? '#fff4d9'
                  : provider === 'azure' ? '#ecf3f8'
                  : T.bgAlt,
        display: 'grid', placeItems: 'center',
        color: T.ink, fontSize: 14, fontWeight: 700,
      }}>
        {modeLabel(d?.target_mode || 'prove7_hosted').charAt(0) === 'S' ? '▦' : '☁'}
      </div>

      <div>
        <Link
          to={`/cloud-operations/advanced/deployments/${d.id}`}
          style={{
            fontSize: 14, fontWeight: 700, color: T.ink,
            textDecoration: 'none',
          }}
        >
          {d.name || d.id?.slice(0, 8)}
        </Link>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, fontSize: 12, color: T.text2 }}>
          {d.target_mode && <ModePill mode={d.target_mode} />}
          {provider && <ProviderPill provider={provider} />}
          <span>· {region}</span>
          {sizeTag && <span>· {sizeTag}</span>}
          <span>· v{version}</span>
        </div>
      </div>

      <StatusPill status={d.status || 'pending'} />

      <div style={{ textAlign: 'right', minWidth: 56 }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: T.ink }}>
          {cpu == null ? '—' : `${cpu}%`}
        </div>
        <div style={{ fontSize: 11, color: T.text3 }}>CPU</div>
      </div>

      <div style={{ textAlign: 'right', minWidth: 56 }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: T.ink }}>
          {mem == null ? '—' : `${mem}%`}
        </div>
        <div style={{ fontSize: 11, color: T.text3 }}>Memory</div>
      </div>

      <Link
        to={`/cloud-operations/advanced/deployments/${d.id}`}
        style={{ ...btnSm, textDecoration: 'none' }}
      >
        Logs
      </Link>

      <Link
        to={`/cloud-operations/advanced/deployments/${d.id}`}
        style={{ ...btnSm, textDecoration: 'none' }}
      >
        Scale
      </Link>

      {stopped ? (
        <button type="button" style={{ ...btnSm, background: '#1f8a4c', color: '#fff', borderColor: '#1f8a4c' }}>
          Start
        </button>
      ) : (
        <button type="button" style={{ ...btnSm, background: T.danger, color: '#fff', borderColor: T.danger }}>
          Stop
        </button>
      )}
    </div>
  );
}

function providerFromDeployment(d) {
  // The deployment row doesn't carry provider directly; the target does. The
  // detail page hydrates target for the same id; for the list we fall back
  // to inferring from resource_handles if present.
  if (d.provider) return d.provider;
  const handles = d.resource_handles;
  if (handles?.cluster_self_link?.includes('container.googleapis.com')) return 'gcp';
  if (handles?.cluster_self_link?.includes('eks.amazonaws.com'))         return 'aws';
  if (handles?.cluster_self_link?.includes('azmk8s.io'))                 return 'azure';
  return null;
}

function regionFromDeployment(d) {
  return d.primary_region || d.region || d.resource_handles?.region || '—';
}

function sizeTagFromDeployment(d) {
  return d.instance_size || null;
}
